import { useEffect, useRef, useState } from 'react';
import { formatBytes } from '../../lib/format.js';
import { useSession } from '../../lib/auth/session.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getAccessToken } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, CardHead, SectionHead } from '../../components/ui/Primitives.js';
import { toastError } from '../../components/ui/Toast.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { FileField } from '../../components/ui/FileField.js';
import { formatNumber } from '../../lib/format.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import {
  useRemoveVoiceKey, useSaveVoiceKey, useSaveVoiceSettings, useTranscribeMedia, useUpdateMedia, useVoice, useVoices,
} from '../../lib/api/hooks-voice.js';
import { FromTextForm, usageLine, useVoicePreview } from '../../components/telephony/MakeRecording.js';

/**
 * Uploaded audio.
 *
 * One screen for everything that plays to a caller, filtered by what it is for.
 * Splitting prompts, greetings and hold music into separate pages would mean
 * three uploaders and three empty states for one job.
 *
 * Audio is converted to 8 kHz mono on the way in, so what is listed here is
 * what the switch will actually play — not what was uploaded.
 */

interface MediaFile {
  id: string; kind: string; name: string;
  /** What it says (0160), and where those words came from. */
  description: string | null; description_source?: 'typed' | 'transcribed' | 'generated' | null;
  bytes: number | null; duration_sec: number | null;
  sample_rate: number | null; channels: number | null;
  original_name: string | null; created_at: string;
}

const KINDS = [
  { key: 'greeting', label: 'Greetings', icon: 'ivr',
    hint: 'Played when an IVR answers' },
  { key: 'prompt', label: 'Prompts', icon: 'media',
    hint: 'Menu options and instructions' },
  { key: 'moh', label: 'Music on hold', icon: 'clock',
    hint: 'Played while a caller waits' },
  { key: 'announcement', label: 'Announcements', icon: 'phone',
    hint: 'Notices before a call connects' },
] as const;

/** Its own KB-only formatter read "28000 KB" for a wideband file; the shared one says 27.3 MB. */
const fmtBytes = (n: number | null): string => (n === null ? '—' : formatBytes(n));

const fmtDuration = (s: number | null | undefined): string => {
  if (s == null || !Number.isFinite(s)) return '—';
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};

/** The time on a player: 0:04, 1:12. */
const clock = (s: number): string => {
  const t = Math.max(0, Math.floor(s));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

export function MediaTab({ tenantId, section = 'prompts' }: {
  tenantId: string;
  /** Prompts and greetings, or music on hold: two screens over one library (docs/26 E2). */
  section?: 'prompts' | 'moh';
}) {
  const qc = useQueryClient();
  // Uploading and deleting need extension:write, the account's hold music tenant:write, as the API
  // requires (permission sweep, 19 September 2026).
  const can = useSession((s) => s.can);
  const mayUpload = can('extension:write');
  const kinds = KINDS.filter((k) => (section === 'moh') === (k.key === 'moh'));
  const [kind, setKind] = useState<string>(section === 'moh' ? 'moh' : 'greeting');
  /** The file loaded into the player, with an object URL of its audio. */
  const [current, setCurrent] = useState<{ id: string; url: string } | null>(null);
  const [playback, setPlayback] = useState({ playing: false, time: 0, duration: 0 });
  const [loading, setLoading] = useState<string | null>(null);
  const [playError, setPlayError] = useState<{ id: string; message: string } | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  // An object URL holds the audio in memory until it is revoked: the old one
  // goes when another file loads, and the last one when the page closes.
  useEffect(() => () => { if (current) URL.revokeObjectURL(current.url); }, [current]);
  const voice = useVoice(tenantId);
  const updateMedia = useUpdateMedia(tenantId);
  const transcribe = useTranscribeMedia(tenantId);
  /** The recording whose words are being edited, and the words so far. */
  const [words, setWords] = useState<{ id: string; text: string } | null>(null);

  const q = useQuery({
    queryKey: ['media', tenantId],
    queryFn: () => api.get<{
      data: MediaFile[]; conversion_available: boolean;
      /** What this node's sox can actually read. Absent on an older API. */
      formats?: string[]; max_bytes?: number;
      /** The account's hold music. Absent on an older API. */
      moh_default_id?: string | null;
    }>(`/v1/tenants/${tenantId}/media`),
    enabled: !!tenantId,
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete<{ references_cleared: number }>(
      `/v1/tenants/${tenantId}/media/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['media', tenantId] }); },
  });

  const setDefault = useMutation({
    mutationFn: (mediaId: string | null) => api.put<{ moh_default_id: string | null }>(
      `/v1/tenants/${tenantId}/media/moh-default`, { media_id: mediaId }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['media', tenantId] }); },
  });

  const files = (q.data?.data ?? []).filter((f) => f.kind === kind);

  /**
   * Plays a file in its own row: its button turns to pause, and a seek bar and
   * the time appear on the row, which keeps its height. Playing another file
   * stops this one. It was a player that opened under the row, which pushed the
   * list about while checking one greeting after another (14 September 2026).
   *
   * Loaded as a blob rather than pointing <audio> at the URL: the endpoint needs
   * an Authorization header, which an <audio src> cannot send. Playing a blob
   * needs `media-src blob:` in the CSP (roles/nginx, security-headers), which it
   * never had -- and failures returned without a word -- so the button did
   * nothing and said nothing (14 September 2026).
   */
  const play = async (f: MediaFile): Promise<void> => {
    const el = audio.current;
    if (!el) return;
    setPlayError(null);
    if (current?.id === f.id) {
      if (el.paused) {
        if (el.ended) el.currentTime = 0;
        void el.play();
      } else {
        el.pause();
      }
      return;
    }
    el.pause();
    setLoading(f.id);
    try {
      const res = await fetch(`/api/v1/tenants/${tenantId}/media/${f.id}/audio`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) {
        setPlayError({
          id: f.id,
          message: res.status === 404
            ? 'The record exists but its audio is missing on the node.'
            : res.status === 401
              ? 'Your session has expired. Reload the page and try again.'
              : `The audio could not be loaded (${res.status}).`,
        });
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      setPlayback({ playing: false, time: 0, duration: f.duration_sec ?? 0 });
      setCurrent({ id: f.id, url });
      el.src = url;
      await el.play();
    } catch (e) {
      // A browser that wants a fresh click after the download: the file is
      // loaded, so the next press plays it.
      setPlayError({
        id: f.id,
        message: e instanceof DOMException && e.name === 'NotAllowedError'
          ? 'Press play again to start it.'
          : 'The audio could not be played.',
      });
    } finally {
      setLoading(null);
    }
  };

  /** Jumps to a fraction of the way through the file that is loaded. */
  const seek = (fraction: number) => {
    const el = audio.current;
    const d = playback.duration || el?.duration || 0;
    if (!el || !Number.isFinite(d) || d <= 0) return;
    el.currentTime = Math.max(0, Math.min(d, fraction * d));
  };

  return (
    <>
      <SectionHead title={section === 'moh' ? 'Music on hold' : 'Prompts & greetings'}
        sub={<>
          {section === 'moh'
          ? 'What a caller hears while they wait.'
          : 'What a caller hears from menus, greetings and announcements.'}
          {' '}Uploads are converted to 48 kHz mono Ogg, which is what the switch plays.
        </>} />

      {/* Problems first, before anything they affect. */}
      {q.error && <ErrorBanner error={q.error} />}

      {q.data && !q.data.conversion_available && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            Audio conversion is unavailable on this node, so uploads will be
            refused rather than stored in a format the switch may not play.
          </span>
        </div>
      )}

      {/* The account's hold music: what a held caller hears, and what a queue
          with no music of its own plays (docs/26 E2). */}
      {section === 'moh' && q.data && (
        <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
          <div className="arc-field" style={{ margin: 0, maxWidth: 420 }}>
            <label className="arc-label" htmlFor="moh-default">Hold music for this account</label>
            <select id="moh-default" className="arc-select"
              value={q.data.moh_default_id ?? ''} disabled={setDefault.isPending || !can('tenant:write')}
              onChange={(e) => setDefault.mutate(e.target.value || null)}>
              <option value="">Platform music</option>
              {q.data.data.filter((m) => m.kind === 'moh').map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
            <p className="arc-hint">Plays to held callers, and in queues without music of their own.</p>
          </div>
        </div>
      )}

      {/* Cards rather than a row of pills. Four kinds with different purposes
          is a choice, not a filter, and the hint that explains each one has
          nowhere to live on a button. */}
      {kinds.length > 1 && (
      <div className="arc-kind-grid">
        {kinds.map((k) => {
          const n = (q.data?.data ?? []).filter((f) => f.kind === k.key).length;
          return (
            <button key={k.key} type="button" className="arc-kind"
              data-on={kind === k.key} aria-pressed={kind === k.key}
              onClick={() => setKind(k.key)}>
              <span className="arc-kind-icon"><Icon name={k.icon} size={18} /></span>
              <span className="arc-kind-body">
                <span className="arc-kind-label">{k.label}</span>
                <span className="arc-kind-hint">{k.hint}</span>
              </span>
              <span className="arc-kind-count">{n}</span>
            </button>
          );
        })}
      </div>
      )}

      {mayUpload && (
        <UploadCard tenantId={tenantId} kind={kind} existing={files}
          formats={q.data?.formats}
          maxBytes={q.data?.max_bytes}
          onDone={() => void qc.invalidateQueries({ queryKey: ['media', tenantId] })} />
      )}

      {mayUpload && kind !== 'moh' && voice.data?.key && (
        <div className="arc-card" style={{ marginBottom: 14 }}>
          <CardHead title="Make one from text" sub="Type what it should say, and it is spoken in the account's voice." />
          <div className="arc-card-body">
            <FromTextForm key={kind} tenantId={tenantId} kind={kind as 'prompt' | 'greeting' | 'announcement'} idPrefix="md-tts" onMade={() => undefined} />
          </div>
        </div>
      )}

      <div className="arc-card">
        <CardHead title={KINDS.find((k) => k.key === kind)?.label} sub={KINDS.find((k) => k.key === kind)?.hint} />
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th style={{ width: 44 }} />
                <th>Name</th><th>Uploaded from</th>
                <th className="arc-td-num">Length</th>
                <th className="arc-td-num">Size</th>
                <th />
              </tr>
            </thead>
            {q.data === undefined ? <TableSkeleton cols={6} /> : (
              <tbody>
                {files.map((f) => {
                  const isCurrent = current?.id === f.id;
                  const playing = isCurrent && playback.playing;
                  const length = isCurrent ? playback.duration || f.duration_sec || 0 : 0;
                  return (
                    <tr key={f.id}>
                      <td>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                          aria-label={playing ? `Pause ${f.name}` : `Play ${f.name}`}
                          title={playing ? 'Pause' : 'Play'}
                          disabled={loading === f.id}
                          onClick={() => void play(f)}>
                          <Icon name={playing ? 'pause' : 'play'} size={15} />
                        </button>
                      </td>
                      <td className={isCurrent ? 'arc-audio-cell' : undefined}>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 550 }}>{f.name}</span>
                          {words?.id === f.id ? (
                            <form className="arc-media-words-edit" onSubmit={(e) => {
                              e.preventDefault();
                              updateMedia.mutate({ id: f.id, description: words.text.trim() || null }, { onSuccess: () => setWords(null) });
                            }}>
                              <label className="arc-label" htmlFor={`mw-${f.id}`}>What it says</label>
                              <textarea id={`mw-${f.id}`} className="arc-input" rows={3} maxLength={1000} autoFocus value={words.text}
                                onChange={(e) => setWords({ id: f.id, text: e.target.value })} />
                              <span className="arc-row" style={{ gap: 6 }}>
                                <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={updateMedia.isPending}>Save</button>
                                <button type="button" className="arc-btn arc-btn-sm" onClick={() => setWords(null)}>Cancel</button>
                              </span>
                            </form>
                          ) : f.description ? (
                            <span className="arc-media-words">
                              “{f.description}”
                              <span className="arc-subtle"> · {f.description_source === 'transcribed' ? 'heard in the recording'
                                : f.description_source === 'generated' ? 'made from these words' : 'typed'}</span>
                            </span>
                          ) : f.kind !== 'moh' ? (
                            <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                              No words yet. The AI flow builder picks a recording by what it says.
                            </span>
                          ) : null}
                          {playError?.id === f.id && (
                            <span className="arc-error" style={{ margin: 0 }} role="alert">{playError.message}</span>
                          )}
                        </div>
                        {isCurrent && length > 0 && (
                          <div className="arc-audio-seek" role="slider" tabIndex={0}
                            aria-label={`Position in ${f.name}`}
                            aria-valuemin={0} aria-valuemax={Math.round(length)}
                            aria-valuenow={Math.round(playback.time)}
                            aria-valuetext={`${clock(playback.time)} of ${clock(length)}`}
                            onClick={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              seek((e.clientX - r.left) / r.width);
                            }}
                            onKeyDown={(e) => {
                              const to = e.key === 'ArrowRight' ? (playback.time + 5) / length
                                : e.key === 'ArrowLeft' ? (playback.time - 5) / length
                                  : e.key === 'Home' ? 0 : e.key === 'End' ? 1 : null;
                              if (to === null) return;
                              e.preventDefault();
                              seek(to);
                            }}>
                            <div className="arc-audio-seek-fill"
                              style={{ width: `${Math.min(100, (playback.time / length) * 100)}%` }} />
                          </div>
                        )}
                      </td>
                      <td className="arc-muted arc-truncate" style={{ maxWidth: 200 }}>
                        {f.original_name ?? <span className="arc-subtle">—</span>}
                      </td>
                      <td className="arc-td-num arc-muted">
                        {isCurrent && length > 0
                          ? <span className="arc-num">{clock(playback.time)} / {clock(length)}</span>
                          : fmtDuration(f.duration_sec)}
                      </td>
                      <td className="arc-td-num arc-muted">{fmtBytes(f.bytes)}</td>
                      <td className="arc-td-right">
                        <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end' }}>
                          {mayUpload && f.kind !== 'moh' && voice.data?.transcription && !f.description && (
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={transcribe.isPending}
                              onClick={() => transcribe.mutate(f.id)}>
                              {transcribe.isPending && transcribe.variables === f.id ? 'Listening…' : 'Fill in from the recording'}
                            </button>
                          )}
                          {mayUpload && f.kind !== 'moh' && (
                            <RowAction kind="edit" label={`Edit what ${f.name} says`}
                              onClick={() => setWords({ id: f.id, text: f.description ?? '' })} />
                          )}
                          {mayUpload && <RowAction kind="delete" label={`Delete ${f.name}`}
                            onClick={() => {
                              void confirmAction({
                                title: `Delete "${f.name}"?`,
                                body: 'Anything using it falls back to silence.',
                                action: 'Delete', danger: true,
                              }).then((ok) => {
                                if (ok) {
                                  if (isCurrent) { audio.current?.pause(); setCurrent(null); }
                                  del.mutate(f.id);
                                }
                              });
                            }} />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {q.data !== undefined && files.length === 0 && (
          <Empty icon="media" title={`No ${KINDS.find((k) => k.key === kind)?.label.toLowerCase()}`}
            hint="Upload a WAV, MP3, FLAC or OGG file above." />
        )}
      </div>

      {section === 'prompts' && <VoiceCard tenantId={tenantId} />}

      {/* One element for the page: playing another file stops this one, so a
          page of prompts never talks over itself. */}
      <audio ref={audio} hidden preload="auto"
        onPlay={() => setPlayback((p) => ({ ...p, playing: true }))}
        onPause={() => setPlayback((p) => ({ ...p, playing: false }))}
        onEnded={() => setPlayback((p) => ({ ...p, playing: false, time: p.duration }))}
        onTimeUpdate={(e) => {
          const time = e.currentTarget.currentTime;
          setPlayback((p) => ({ ...p, time }));
        }}
        onLoadedMetadata={(e) => {
          const duration = e.currentTarget.duration;
          if (Number.isFinite(duration) && duration > 0) setPlayback((p) => ({ ...p, duration }));
        }}
        onError={() => {
          if (current) setPlayError({ id: current.id, message: 'This browser could not play the file.' });
        }} />
    </>
  );
}

function UploadCard({ tenantId, kind, existing, onDone, formats, maxBytes }: {
  tenantId: string; kind: string; onDone: () => void;
  /** This kind's files: a name already among them replaces that file's audio. */
  existing: Array<{ id: string; name: string }>;
  /** What this node can read, from the API. Undefined until the list loads. */
  formats?: string[] | undefined;
  maxBytes?: number | undefined;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const replacing = existing.some((f) => f.name.trim().toLowerCase() === name.trim().toLowerCase());

  /**
   * Undefined means the list has not loaded yet, not that nothing is supported --
   * so the input stays permissive until the node has answered, rather than
   * briefly refusing everything.
   */
  const accept = formats && formats.length > 0
    ? `audio/*,${formats.map((f) => `.${f}`).join(',')}`
    : 'audio/*';
  const mb = Math.round((maxBytes ?? 5 * 1024 * 1024) / 1024 / 1024);
  const hint = formats === undefined
    ? `Up to ${mb} MB.`
    : formats.length === 0
      ? 'This node has no audio conversion tooling, so no upload will be accepted.'
      : `${formats.map((f) => f.toUpperCase()).join(', ')}. Up to ${mb} MB.`;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!file) return;
    /**
     * The same name as a file already here replaces that file's audio (owner, 30 September 2026:
     * "it didnt overwrite the old file"). Its id stays, so whatever uses it plays the new
     * recording; asked first, because the old audio is gone once it is replaced.
     */
    const same = existing.find((f) => f.name.trim().toLowerCase() === name.trim().toLowerCase());
    if (same && !await confirmAction({
      title: `Replace the audio of "${same.name}"?`,
      body: 'Everything that uses it plays the new recording from the next call. The old audio is not kept.',
      action: 'Replace',
    })) return;
    setBusy(true);
    try {
      const body = new FormData();
      if (!same) {
        body.append('kind', kind);
        body.append('name', name);
        if (description) body.append('description', description);
      }
      body.append('file', file);

      // FormData, so no Content-Type header: the browser has to set it with
      // the multipart boundary, and naming it ourselves omits the boundary and
      // makes the server unable to parse a body it just received.
      const res = await fetch(same
        ? `/api/v1/tenants/${tenantId}/media/${same.id}/audio`
        : `/api/v1/tenants/${tenantId}/media`, {
        method: same ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body,
      });
      if (!res.ok) {
        const problem = await res.json().catch(() => null);
        throw new Error(problem?.detail ?? problem?.title ?? `Upload failed (${res.status})`);
      }
      setName(''); setDescription(''); setFile(null);
      onDone();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }} onSubmit={submit}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr) minmax(0,1.4fr) auto',
        gap: 12, alignItems: 'start',
      }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="md-name">Name</label>
          <input id="md-name" className="arc-input" required value={name}
            onChange={(e) => setName(e.target.value)} placeholder={kind === 'moh' ? 'Calm piano' : 'Main greeting'} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="md-desc">What it says</label>
          <input id="md-desc" className="arc-input" value={description} maxLength={1000}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional" />
          <p className="arc-hint">Left empty, it is filled in from the recording when the account has a transcription key.</p>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="md-file">Audio file</label>
          {/*
            Built from what the node answered, not from a list written here.
            The hard-coded version offered MP3 on a node whose sox could not
            decode one, and the refusal read as though the file were at fault --
            which is the bug this screen was reported for.
          */}
          <FileField id="md-file" accept={accept} fileName={file?.name}
            onFile={(f) => {
              setFile(f);
              // Saves typing the obvious thing: the filename is nearly always
              // what the prompt should be called.
              if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
            }} />
          <p className="arc-hint">{hint} The name of an existing file replaces its audio.</p>
        </div>
        <div style={{ paddingTop: 25 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={busy || !file}>
            {busy ? 'Uploading…' : replacing ? 'Replace' : 'Upload'}
          </button>
        </div>
      </div>
    </form>
  );
}

/**
 * The voice recordings are made from text in (owner, 7 October 2026): whose ElevenLabs key, the
 * house voice, and the limits. The key is the account's own or the platform's; the platform alone
 * sets the limit on its key, and the account may set one on its own.
 */
function VoiceCard({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const status = useVoice(tenantId);
  const s = status.data;
  const voices = useVoices(tenantId, !!s?.key);
  const save = useSaveVoiceSettings(tenantId);
  const saveKey = useSaveVoiceKey(tenantId);
  const removeKey = useRemoveVoiceKey(tenantId);
  const step = useStepUp();
  const preview = useVoicePreview(tenantId);
  const [keyOpen, setKeyOpen] = useState(false);
  const [secret, setSecret] = useState('');
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const [own, setOwn] = useState<string | null>(null);
  const [platform, setPlatform] = useState<string | null>(null);
  const mayKeys = can('qa:provider:write');
  const maySettings = can('tenant:write');
  if (!s) return null;

  const list = voices.data?.data ?? [];
  const chosen = voiceId ?? s.voice?.id ?? '';
  const keyText = s.account_key ? `This account’s key (…${s.account_key.hint ?? ''})`
    : s.key === 'platform' ? 'The platform’s key' : 'No key yet';
  const ownCap = own ?? (s.caps.own === null ? '' : String(s.caps.own));

  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <CardHead title="Recordings from text" sub="ElevenLabs speaks the words you type. These are the account’s voice and its limits." />
      <div className="arc-card-body arc-stack" style={{ gap: 16 }}>
        <div className="arc-voice-row">
          <div>
            <div className="arc-label" style={{ margin: 0 }}>ElevenLabs key</div>
            <div style={{ fontSize: 13 }}>{keyText}</div>
            <p className="arc-hint" style={{ margin: 0 }}>
              The account’s own key is used first; the platform’s, where it has one, when the account has none.
            </p>
            {s.account_key?.last_error && <p className="arc-error" style={{ margin: '4px 0 0' }}>{s.account_key.last_error}</p>}
          </div>
          {mayKeys && (
            <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => setKeyOpen((v) => !v)}>
                {keyOpen ? 'Cancel' : s.account_key ? 'Change key' : 'Add a key'}
              </button>
              {s.account_key && (
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={() => { void confirmAction({ title: 'Remove the ElevenLabs key?', body: 'Recordings already made stay. New ones use the platform’s key, where it has one.', action: 'Remove', danger: true }).then((ok) => { if (ok) void step.run(() => removeKey.mutateAsync()); }); }}>
                  Remove key
                </button>
              )}
            </div>
          )}
        </div>
        {keyOpen && (
          <form className="arc-row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }} onSubmit={(e) => {
            e.preventDefault();
            void step.run(() => saveKey.mutateAsync({ secret })).then((ok) => { if (ok) { setSecret(''); setKeyOpen(false); } });
          }}>
            <div className="arc-field" style={{ margin: 0, minWidth: 280 }}>
              <label className="arc-label" htmlFor="vk-secret">API key</label>
              <input id="vk-secret" className="arc-input arc-mono" type="password" autoComplete="off" required value={secret}
                onChange={(e) => setSecret(e.target.value)} />
              <p className="arc-hint" style={{ margin: 0 }}>Checked with ElevenLabs before it is kept. Words typed for a recording are sent to ElevenLabs.</p>
            </div>
            <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={saveKey.isPending || step.busy || secret.length < 8}>
              {saveKey.isPending ? 'Checking…' : 'Save'}
            </button>
            {saveKey.error && <p className="arc-error" style={{ margin: 0, flexBasis: '100%' }}>{(saveKey.error as Error).message}</p>}
          </form>
        )}

        {s.key && (
          <div className="arc-voice-row">
            <div className="arc-field" style={{ margin: 0, flex: 1, minWidth: 240, maxWidth: 420 }}>
              <label className="arc-label" htmlFor="vk-voice">House voice</label>
              <div className="arc-row" style={{ gap: 8 }}>
                <select id="vk-voice" className="arc-select" style={{ flex: 1, minWidth: 0 }} value={chosen} disabled={!maySettings || voices.isLoading}
                  onChange={(e) => setVoiceId(e.target.value)}>
                  {!chosen && <option value="">Choose a voice…</option>}
                  {s.voice && !list.some((v) => v.voice_id === s.voice!.id) && <option value={s.voice.id}>{s.voice.name}</option>}
                  {list.map((v) => (
                    <option key={v.voice_id} value={v.voice_id}>
                      {v.name}{[v.accent, v.gender, v.age].filter(Boolean).length ? ` · ${[v.accent, v.gender, v.age].filter(Boolean).join(', ')}` : ''}
                    </option>
                  ))}
                </select>
                <button type="button" className="arc-btn arc-btn-sm" disabled={!chosen} onClick={() => { void preview.play(chosen); }}>
                  <Icon name={preview.playing === chosen ? 'pause' : 'play'} size={12} />{preview.playing === chosen ? 'Stop' : 'Listen'}
                </button>
                {maySettings && voiceId && voiceId !== s.voice?.id && (
                  <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={save.isPending}
                    onClick={() => save.mutate({ voice_id: voiceId, voice_name: list.find((v) => v.voice_id === voiceId)?.name ?? voiceId },
                      { onSuccess: () => setVoiceId(null) })}>Save</button>
                )}
              </div>
              <p className="arc-hint" style={{ margin: 0 }}>Every recording made from text uses it unless another is chosen, so the prompts sound like one person.</p>
              {voices.error && <p className="arc-error" style={{ margin: 0 }}>{(voices.error as Error).message}</p>}
              {preview.error && <p className="arc-error" style={{ margin: 0 }}>{preview.error}</p>}
            </div>
          </div>
        )}

        <form className="arc-voice-row" style={{ justifyContent: 'flex-start' }} onSubmit={(e) => {
          e.preventDefault();
          save.mutate({
            ...(own !== null ? { own_monthly_chars: own.trim() === '' ? null : Number(own) } : {}),
            ...(platform !== null ? { platform_monthly_chars: Number(platform || 0) } : {}),
          }, { onSuccess: () => { setOwn(null); setPlatform(null); } });
        }}>
          <div style={{ flexBasis: '100%' }}>
            <div className="arc-label" style={{ margin: 0 }}>Limits</div>
            <p style={{ margin: 0, fontSize: 13 }}>{s.key ? usageLine(s) : 'Nothing made this month.'}</p>
            <p className="arc-hint" style={{ margin: 0 }}>A recording is at most {formatNumber(s.per_recording)} characters. Months run on South African time.</p>
          </div>
          <div className="arc-field" style={{ margin: 0, width: 220 }}>
            <label className="arc-label" htmlFor="vk-own">A month on this account’s key</label>
            <input id="vk-own" className="arc-input arc-num" inputMode="numeric" disabled={!maySettings} value={ownCap}
              placeholder="No limit" onChange={(e) => setOwn(e.target.value.replace(/[^0-9]/g, ''))} />
            <p className="arc-hint" style={{ margin: 0 }}>Characters. Empty for no limit: ElevenLabs bills the account.</p>
          </div>
          {s.platform_view && (
            <div className="arc-field" style={{ margin: 0, width: 220 }}>
              <label className="arc-label" htmlFor="vk-platform">A month on the platform’s key</label>
              <input id="vk-platform" className="arc-input arc-num" inputMode="numeric" value={platform ?? String(s.caps.platform)}
                onChange={(e) => setPlatform(e.target.value.replace(/[^0-9]/g, ''))} />
              <p className="arc-hint" style={{ margin: 0 }}>Characters, for this account. Only the platform sees this.</p>
            </div>
          )}
          {(maySettings || s.platform_view) && (
            <div style={{ alignSelf: 'flex-start', paddingTop: 22 }}>
              <button type="submit" className="arc-btn arc-btn-sm" disabled={save.isPending || (own === null && platform === null)}>Save limits</button>
            </div>
          )}
          {save.error && <p className="arc-error" style={{ margin: 0, flexBasis: '100%' }}>{(save.error as Error).message}</p>}
        </form>
      </div>
      <StepUpPrompt step={step} what="Changing the ElevenLabs key">A provider key is a credential.</StepUpPrompt>
    </div>
  );
}
